import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const DEVICE_TYPES = ['zkteco', 'essl', 'hikvision', 'generic_http'];

export default function AttendanceDevices() {
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState({
    name: '', device_type: 'zkteco', ip_address: '',
    port: 4370, serial_number: '', api_key: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mappingDevice, setMappingDevice] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [newMap, setNewMap] = useState({ device_user_id: '', person_type: 'staff', person_id: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    const r = await api.get('/attendance-devices/');
    setDevices(r.data);
  };

  const createDevice = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/attendance-devices/', form);
      setShowForm(false);
      setForm({ name: '', device_type: 'zkteco', ip_address: '', port: 4370, serial_number: '', api_key: '' });
      load();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const deleteDevice = async (id) => {
    if (!window.confirm('Delete this device?')) return;
    await api.delete(`/attendance-devices/${id}`);
    load();
  };

  const syncDevice = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/attendance-devices/${id}/sync`);
      alert(`Synced! Processed ${r.data.processed} of ${r.data.total_records} records.`);
      load();
    } catch (err) { alert('Sync failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const openMappings = async (device) => {
    setMappingDevice(device);
    const r = await api.get(`/attendance-devices/${device.id}/mappings`);
    setMappings(r.data);
  };

  const addMapping = async () => {
    try {
      await api.post(`/attendance-devices/${mappingDevice.id}/mappings`, {
        ...newMap, person_id: parseInt(newMap.person_id),
      });
      setNewMap({ device_user_id: '', person_type: 'staff', person_id: '' });
      openMappings(mappingDevice);
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const deleteMapping = async (id) => {
    await api.delete(`/attendance-devices/mappings/${id}`);
    openMappings(mappingDevice);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">🔒 Biometric Devices</h2>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          <i className="fas fa-plus mr-2"></i> Add Device
        </button>
      </div>

      {showForm && (
        <form onSubmit={createDevice} className="bg-white p-4 rounded shadow mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Device Name" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="p-2 border rounded" />
            <select value={form.device_type}
              onChange={e => setForm({ ...form, device_type: e.target.value })}
              className="p-2 border rounded">
              {DEVICE_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <input placeholder="IP Address" value={form.ip_address}
              onChange={e => setForm({ ...form, ip_address: e.target.value })}
              className="p-2 border rounded" />
            <input type="number" placeholder="Port (default 4370)" value={form.port}
              onChange={e => setForm({ ...form, port: parseInt(e.target.value) || 4370 })}
              className="p-2 border rounded" />
            <input placeholder="Serial Number" value={form.serial_number}
              onChange={e => setForm({ ...form, serial_number: e.target.value })}
              className="p-2 border rounded" />
            <input placeholder="API Key (for push mode)" value={form.api_key}
              onChange={e => setForm({ ...form, api_key: e.target.value })}
              className="p-2 border rounded" />
          </div>
          <button type="submit" disabled={busy}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50">
            {busy ? 'Saving...' : 'Save Device'}
          </button>
        </form>
      )}

      <div className="bg-white p-4 rounded shadow">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Type', 'IP', 'Serial', 'Last Sync', 'Actions'].map(h => (
                <th key={h} className="p-3 text-left font-semibold text-gray-700">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {devices.map(d => (
              <tr key={d.id} className="border-t">
                <td className="p-3 font-medium">{d.name}</td>
                <td className="p-3"><span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{d.device_type}</span></td>
                <td className="p-3 font-mono text-xs">{d.ip_address || '—'}:{d.port || 4370}</td>
                <td className="p-3 font-mono text-xs">{d.serial_number || '—'}</td>
                <td className="p-3 text-xs">{d.last_sync ? new Date(d.last_sync).toLocaleString() : 'Never'}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    {['zkteco', 'essl'].includes(d.device_type) && (
                      <button onClick={() => syncDevice(d.id)} disabled={busy}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded">Sync</button>
                    )}
                    <button onClick={() => openMappings(d)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Map Users</button>
                    <button onClick={() => deleteDevice(d.id)}
                      className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {devices.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-400">No devices registered.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {mappingDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">User Mappings — {mappingDevice.name}</h3>
              <button onClick={() => setMappingDevice(null)} className="text-2xl">×</button>
            </div>

            <p className="text-sm text-gray-500 mb-3">
              Map each biometric enrollment ID to a teacher or staff member.
            </p>

            <div className="grid grid-cols-4 gap-2 mb-4">
              <input placeholder="Device User ID" value={newMap.device_user_id}
                onChange={e => setNewMap({ ...newMap, device_user_id: e.target.value })}
                className="p-2 border rounded text-sm" />
              <select value={newMap.person_type}
                onChange={e => setNewMap({ ...newMap, person_type: e.target.value })}
                className="p-2 border rounded text-sm">
                <option value="teacher">Teacher</option>
                <option value="staff">Staff</option>
              </select>
              <input type="number" placeholder="Person ID" value={newMap.person_id}
                onChange={e => setNewMap({ ...newMap, person_id: e.target.value })}
                className="p-2 border rounded text-sm" />
              <button onClick={addMapping}
                className="bg-blue-600 text-white rounded text-sm">Add</button>
            </div>

            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 text-left">Device ID</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Person ID</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m.id} className="border-t">
                    <td className="p-2 font-mono text-xs">{m.device_user_id}</td>
                    <td className="p-2">{m.person_type}</td>
                    <td className="p-2">{m.person_id}</td>
                    <td className="p-2">
                      <button onClick={() => deleteMapping(m.id)}
                        className="text-red-600 text-xs hover:underline">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}